import axios, { AxiosError } from 'axios';
import { auth } from '../firebase';
import { backendAPI } from './products';

interface ApiError {
  message?: string;
  error?: string;
  code?: string;
}

export type CategoryPayload = {
  businessId?: number;
  name: string;
  slug?: string | null;
  description?: string | null;
  parentCategoryId?: number | null;
  displayOrder?: number;
  active?: boolean;
};

export type CategoryUpdatePayload = Partial<Omit<CategoryPayload, 'businessId'>>;

export interface CategoryRecord {
  id: number;
  businessId: number;
  parentCategoryId: number | null;
  name: string;
  slug: string;
  description: string | null;
  displayOrder: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoryTreeNode extends CategoryRecord {
  children: CategoryTreeNode[];
}

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not logged in');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

function mapCategoryError(error: unknown, fallback: string): Error {
  const axiosError = error as AxiosError<ApiError>;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    fallback;
  return new Error(message);
}

export const list_categories = async (
  businessId: number
): Promise<{ categories: CategoryRecord[]; tree: CategoryTreeNode[] }> => {
  try {
    const response = await backendAPI.get('/categories', {
      params: { businessId },
    });
    return {
      categories: response.data?.categories ?? [],
      tree: response.data?.tree ?? [],
    };
  } catch (error) {
    throw mapCategoryError(error, 'Failed to fetch categories');
  }
};

export const create_category = async (
  businessId: number,
  payload: Omit<CategoryPayload, 'businessId'>
): Promise<CategoryRecord> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.post(
      '/categories',
      { ...payload, businessId },
      { headers }
    );
    return response.data?.category;
  } catch (error) {
    throw mapCategoryError(error, 'Failed to create category');
  }
};

export const update_category = async (
  categoryId: number,
  payload: CategoryUpdatePayload
): Promise<CategoryRecord> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.patch(
      `/categories/${categoryId}`,
      payload,
      { headers }
    );
    return response.data?.category;
  } catch (error) {
    throw mapCategoryError(error, 'Failed to update category');
  }
};

export const delete_category = async (categoryId: number): Promise<void> => {
  try {
    const headers = await authHeaders();
    await backendAPI.delete(`/categories/${categoryId}`, { headers });
  } catch (error) {
    throw mapCategoryError(error, 'Failed to delete category');
  }
};

export const assign_product_to_category = async (
  categoryId: number,
  productId: number
): Promise<void> => {
  try {
    const headers = await authHeaders();
    await backendAPI.post(
      `/categories/${categoryId}/products`,
      { productId },
      { headers }
    );
  } catch (error) {
    throw mapCategoryError(error, 'Failed to assign product');
  }
};

export const remove_product_from_category = async (
  categoryId: number,
  productId: number
): Promise<void> => {
  try {
    const headers = await authHeaders();
    await backendAPI.delete(`/categories/${categoryId}/products/${productId}`, {
      headers,
    });
  } catch (error) {
    throw mapCategoryError(error, 'Failed to remove product');
  }
};

export const list_categories_for_product = async (
  productId: number
): Promise<CategoryRecord[]> => {
  try {
    const response = await backendAPI.get(`/categories/by-product/${productId}`);
    return response.data?.categories ?? [];
  } catch (error) {
    throw mapCategoryError(error, 'Failed to fetch product categories');
  }
};

export const list_products_in_category = async (categoryId: number) => {
  try {
    const response = await backendAPI.get(`/categories/${categoryId}/products`);
    return response.data?.products ?? [];
  } catch (error) {
    throw mapCategoryError(error, 'Failed to fetch category products');
  }
};
